import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  Add,
  Delete,
  ContentCopy,
  Visibility,
  DragIndicator,
  ShortText,
  Subject,
  RadioButtonChecked,
  CheckBox,
  ArrowDropDown,
  Star,
  Today,
  Title,
  Group,
  AdminPanelSettings,
  School,
  PersonSearch,
  Person,
  Numbers,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import swal from "sweetalert";
import "../../../assets/scss/_dynamicform.scss";
import NumberRating from "../../UI/NumberRating";
import StarRating from "../../UI/StarRating";
import ModalWindow from "../../UI/ModalWindow";
import DynamicFormDisplay from "./DynamicFormDisplay";
import CustomPageHeader from "../../common/CustomPageHeader";
import GranularPermissionModal from "../../Permissions/GranularPermissionModal";
import { axiosPost } from "../../../utils/AxiosApi";
import {
  checkWidgetAdminOrNot,
  CONTEXT_CONFIG,
  URL,
} from "../../../utils/Constants";
import { NepaliDatePicker } from "nepali-calander";
import moment from "moment";
import { set } from "lodash";

const generateId = () => Math.random().toString(36).substr(2, 9);
const animatedComponents = makeAnimated();

const QUESTION_TYPES = [
  {
    value: "short_answer",
    label: "Short answer",
    icon: <ShortText fontSize="small" />,
  },
  {
    value: "paragraph",
    label: "Paragraph",
    icon: <Subject fontSize="small" />,
  },
  {
    value: "multiple_choice",
    label: "Multiple choice",
    icon: <RadioButtonChecked fontSize="small" />,
  },
  {
    value: "checkboxes",
    label: "Checkboxes",
    icon: <CheckBox fontSize="small" />,
  },
  {
    value: "dropdown",
    label: "Dropdown",
    icon: <ArrowDropDown fontSize="small" />,
  },
  { value: "rating", label: "Rating", icon: <Star fontSize="small" /> },
  { value: "number", label: "Number", icon: <Numbers fontSize="small" /> },
  { value: "date", label: "Date", icon: <Today fontSize="small" /> },
];

const ROLE_MAP_TO_CODE = {
  management: "RL_ADM",
  teacher: "RL_STF",
  student: "RL_STD",
};
const ROLE_CODE_TO_MAP = {
  RL_ADM: "management",
  RL_STF: "teacher",
  RL_STD: "student",
};

const USER_ROLES = [
  {
    value: "management",
    label: "Management",
    icon: <AdminPanelSettings fontSize="small" />,
  },
  { value: "teacher", label: "Teacher", icon: <School fontSize="small" /> },
  { value: "student", label: "Student", icon: <Person fontSize="small" /> },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 5,
    backgroundColor: state.isDisabled ? "#e9ecef" : "white",
    color: "#495057",
    // borderColor: "#e2e8f0",
    // minHeight: 38,
    // fontSize: "0.875rem",
    // boxShadow: "none",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#126696"
      : state.isFocused
        ? "#d8e7f0"
        : "inherit",
  }),
  menu: (b) => ({ ...b, zIndex: 9999, borderRadius: 8 }),
};

// ─── Option Editor ────────────────────────────────────────────────────────────

const OptionEditor = ({ question, onChange }) => {
  const addOption = () => {
    const opts = [
      ...(question.options || []),
      {
        id: generateId(),
        text: `Option ${(question.options?.length || 0) + 1}`,
      },
    ];
    onChange({ options: opts });
  };
  const updateOption = (id, text) =>
    onChange({
      options: question.options.map((o) => (o.id === id ? { ...o, text } : o)),
    });
  const removeOption = (id) => {
    if (question.options.length <= 1) return;
    onChange({ options: question.options.filter((o) => o.id !== id) });
  };

  return (
    <div className="df-options">
      {question.options?.map((opt, idx) => (
        <div key={opt.id} className="df-options__row">
          {question.type === "multiple_choice" && (
            <RadioButtonChecked fontSize="small" className="df-option-icon" />
          )}
          {question.type === "checkboxes" && (
            <CheckBox fontSize="small" className="df-option-icon" />
          )}
          {question.type === "dropdown" && (
            <span className="df-label" style={{ marginBottom: 0 }}>
              {idx + 1}.
            </span>
          )}
          <input
            type="text"
            value={opt.text}
            onChange={(e) => updateOption(opt.id, e.target.value)}
          />
          <button
            className="df-ghost-btn df-ghost-btn--danger"
            onClick={() => removeOption(opt.id)}
            disabled={question.options.length <= 1}
          >
            <Delete fontSize="small" />
          </button>
        </div>
      ))}
      <button className="df-icon-add-btn" onClick={addOption}>
        <Add fontSize="small" /> Add option
      </button>
    </div>
  );
};

// ─── Section Card ─────────────────────────────────────────────────────────────

const SectionCard = ({
  section,
  isActive,
  onClick,
  onUpdate,
  onDelete,
  provided,
  innerRef,
}) => (
  <div
    ref={innerRef}
    {...provided.draggableProps}
    // Only fire onClick when the click originated from the card body,
    // not from the drag handle (which stops propagation on mousedown).
    onClick={onClick}
    data-id={section.id}
    className={`df-card df-card--section${isActive ? " df-card--active" : ""}`}
    style={{ ...provided.draggableProps.style }}
  >
    {/* Drag handle – stopPropagation on mousedown prevents the card's
        onClick from firing when the user initiates a drag. */}
    <div
      {...provided.dragHandleProps}
      className="df-card__drag-handle"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <DragIndicator />
    </div>
    <div className="df-card__body">
      <input
        type="text"
        className="df-input df-input--bare df-input--title"
        value={section.label}
        onChange={(e) => onUpdate(section.id, { label: e.target.value })}
        placeholder="Section Title"
      />
      <input
        type="text"
        className="df-input df-input--bare df-input--description"
        value={section.description}
        onChange={(e) => onUpdate(section.id, { description: e.target.value })}
        placeholder="Description (optional)"
        style={{ marginTop: 10 }}
      />
      {isActive && (
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}
        >
          <button
            className="df-ghost-btn df-ghost-btn--danger"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(section.id);
            }}
          >
            <Delete fontSize="small" />
          </button>
        </div>
      )}
    </div>
  </div>
);

// ─── Question Card ────────────────────────────────────────────────────────────

const QuestionCard = ({
  question,
  isActive,
  onClick,
  onUpdate,
  onDelete,
  onDuplicate,
  provided,
  innerRef,
}) => {
  const upd = (field, val) => onUpdate(question.id, { [field]: val });

  return (
    <div
      ref={innerRef}
      {...provided.draggableProps}
      onClick={onClick}
      data-id={question.id}
      className={`df-card${isActive ? " df-card--active" : ""}`}
      style={{ ...provided.draggableProps.style }}
    >
      <div
        {...provided.dragHandleProps}
        className="df-card__drag-handle"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <DragIndicator />
      </div>

      <div className="df-card__body">
        <div className="df-question__row">
          <input
            type="text"
            className="df-input df-input--bare"
            placeholder="Question"
            value={question.label}
            onChange={(e) => upd("label", e.target.value)}
            style={{ fontWeight: 500, fontSize: "14px" }}
          />
          <Select
            components={animatedComponents}
            styles={{
              ...selectStyles,
              control: (base, state) => ({
                ...selectStyles.control(base, state),
                width: "150px",
              }),
            }}
            options={QUESTION_TYPES}
            value={
              QUESTION_TYPES?.find((opt) => opt?.value === question.type) ||
              null
            }
            onChange={(option) => upd("type", option?.value)}
          />
          {/* <select
            className="df-select df-question__type-select"
            value={question.type}
            onChange={(e) => upd("type", e.target.value)}
          >
            <option value="">Select type</option>
            {QUESTION_TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select> */}
        </div>

        {/* Type-specific preview / editor */}
        <div>
          {(question.type === "short_answer" ||
            question.type === "paragraph") && (
            <div
              className="df-question__placeholder"
              style={{
                width: question.type === "short_answer" ? "50%" : "100%",
              }}
            >
              {question.type === "short_answer"
                ? "Short answer text"
                : "Long answer text"}
            </div>
          )}

          {["multiple_choice", "checkboxes", "dropdown"].includes(
            question.type,
          ) && (
            <OptionEditor
              question={question}
              onChange={(u) => onUpdate(question.id, u)}
            />
          )}

          {(question.type === "rating" || question.type === "number") && (
            <>
              {/* <div className="df-max-number"> */}
              <label>Scale:</label>
              {() => {
                const arr = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
                const options = arr.map((item) => ({
                  label: item,
                  value: item,
                }));

                return (
                  <Select
                    options={options}
                    value={options.find(
                      (o) => o.value === (question.maxNumber || 5),
                    )}
                    onChange={(option) =>
                      upd("maxNumber", Number(option?.value))
                    }
                    styles={{
                      ...selectStyles,
                      control: (base, state) => ({
                        ...selectStyles.control(base, state),
                        width: "150px",
                      }),
                    }}
                    components={animatedComponents}
                  />
                );
              }}
              {/* <select
                className="df-select"
                style={{ width: 80 }}
                value={question.maxNumber || 5}
                onChange={(e) => upd("maxNumber", Number(e.target.value))}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select> */}
              {question.type === "rating" ? (
                <StarRating value={0} max={question.maxNumber || 5} disabled />
              ) : (
                <NumberRating
                  value={0}
                  max={question.maxNumber || 5}
                  disabled
                />
              )}
              {/* </div> */}
            </>
          )}

          {question.type === "date" && (
            <div
              className="df-flex"
              style={{
                gap: 8,
                color: "#6b7280",
                borderBottom: "1px dotted #e2e8f0",
                width: "50%",
                paddingBottom: 6,
              }}
            >
              <Today fontSize="small" />
              <span style={{ fontSize: "0.875rem" }}>DD/MM/YYYY</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="df-question__footer">
          <label className="df-question__required-toggle">
            <input
              type="checkbox"
              checked={question.required}
              onChange={(e) => upd("required", e.target.checked)}
            />
            Required
          </label>
          <Tooltip title="Duplicate">
            <button
              className="df-ghost-btn df-ghost-btn--primary"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(question);
              }}
            >
              <ContentCopy fontSize="small" />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button
              className="df-ghost-btn df-ghost-btn--danger"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(question.id);
              }}
            >
              <Delete fontSize="small" />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

// ─── Context Fields (builder preview) ────────────────────────────────────────

const ContextFields = ({
  fields,
  accentColor,
  contextData,
  contextSelections,
  onContextChange,
  nextTermTarget,
  onNextTermTargetChange,
}) => {
  const FIELD_META = {
    teacher: {
      placeholder: "Select Teacher",
      label: "Teacher",
      dataKey: "teachers",
    },
    subject: {
      placeholder: "Select Subject",
      label: "Subject",
      dataKey: "subjects",
    },
    class: { placeholder: "Select Class", label: "Class", dataKey: "classes" },
    student: {
      placeholder: "Select Student",
      label: "Student",
      dataKey: "students",
    },
    managementMember: {
      placeholder: "Select Management Member",
      label: "Management Member",
      dataKey: "managementMembers",
    },
  };

  return (
    <div
      className="df-context-card"
      style={{ borderLeftColor: accentColor, marginBottom: 16 }}
    >
      <div className="df-context-card__header" style={{ color: accentColor }}>
        Context &amp; Scope
      </div>
      <div className="df-context-card__body">
        {fields.map((field) => {
          if (field === "nextTermTarget") return null;
          const meta = FIELD_META[field];
          if (!meta) return null;
          return (
            <div className="df-context-card__field" key={field}>
              <label className="df-label">{meta.label}</label>
              <Select
                components={animatedComponents}
                value={contextSelections[field] || null}
                options={contextData[meta.dataKey] || []}
                onChange={(v) => onContextChange(field, v)}
                placeholder={meta.placeholder}
                isClearable
                styles={selectStyles}
              />
            </div>
          );
        })}

        {fields.includes("nextTermTarget") && (
          <div className="df-context-card__field">
            <label className="df-label">
              Next Term Target <span style={{ color: accentColor }}>(%)</span>
            </label>
            <div className="df-context-card__percent-input">
              <input
                type="number"
                min={0}
                max={100}
                placeholder="e.g. 85"
                value={nextTermTarget}
                onChange={(e) => onNextTermTargetChange(e.target.value)}
              />
              <span style={{ background: accentColor }}>%</span>
            </div>
            {nextTermTarget > 0 && (
              <div className="df-context-card__progress">
                <div
                  className="df-context-card__progress-bar"
                  style={{
                    width: `${Math.min(nextTermTarget, 100)}%`,
                    background: accentColor,
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Evaluation Setup Card ───────────────────────────────────────────────────

const EvaluationSetupCard = ({
  evaluatorRole,
  evaluateeRole,
  onEvaluatorChange,
  onEvaluateeChange,
  contextData,
  contextSelections,
  onContextChange,
  nextTermTarget,
  onNextTermTargetChange,
  allowSelfEval,
  onAllowSelfEvalChange,
}) => {
  const evaluateeOptions = USER_ROLES.filter(
    (r) => r.value !== evaluatorRole?.value,
  );
  const config =
    evaluatorRole && evaluateeRole
      ? CONTEXT_CONFIG[evaluatorRole.value]?.[evaluateeRole.value]
      : null;

  // const formatOptionLabel = ({ label, icon }) => (
  //   <div className="df-flex" style={{ gap: 8 }}>
  //     {icon}
  //     <span>{label}</span>
  //   </div>
  // );

  return (
    <>
      <div className="df-eval-setup" style={{ marginBottom: 16 }}>
        <div className="df-eval-setup__header">
          Evaluation Setup
          {config && (
            <span
              style={{
                background: config.color + "1a",
                color: config.color,
                padding: "2px 10px",
                borderRadius: 999,
                fontSize: "0.7rem",
                fontWeight: 500,
                marginLeft: 8,
              }}
            >
              {config.label}
            </span>
          )}
        </div>

        <div className="df-eval-setup__body">
          <div className="df-context-card__field">
            <label
              className="df-label"
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              {/* <PersonSearch fontSize="small" />  */}
              Evaluator (who fills the form)
            </label>
            <Select
              components={animatedComponents}
              value={evaluatorRole}
              options={USER_ROLES}
              onChange={(v) => {
                onEvaluatorChange(v);
                onEvaluateeChange(null);
              }}
              placeholder="Select evaluator role"
              isClearable
              styles={selectStyles}
              // formatOptionLabel={formatOptionLabel}
            />
          </div>

          <div className="df-context-card__field">
            <label
              className="df-label"
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              {/* <Group fontSize="small" />  */}
              Evaluatee (who is being evaluated)
            </label>
            <Select
              components={animatedComponents}
              value={evaluateeRole}
              options={evaluateeOptions}
              onChange={onEvaluateeChange}
              placeholder="Select evaluatee role"
              isClearable
              isDisabled={!evaluatorRole}
              styles={selectStyles}
              // formatOptionLabel={formatOptionLabel}
            />
          </div>

          {evaluatorRole &&
            evaluateeRole &&
            evaluatorRole.value === evaluateeRole.value && (
              <div className="df-eval-setup__self-eval">
                <input
                  type="checkbox"
                  checked={allowSelfEval}
                  onChange={(e) => onAllowSelfEvalChange(e.target.checked)}
                />
                <span>Allow self-evaluation</span>
              </div>
            )}
        </div>
      </div>

      {!!config?.fields.length && (
        <ContextFields
          fields={config.fields}
          accentColor={config.color}
          contextData={contextData}
          contextSelections={contextSelections}
          onContextChange={onContextChange}
          nextTermTarget={nextTermTarget}
          onNextTermTargetChange={onNextTermTargetChange}
        />
      )}
    </>
  );
};

// ─── Main Builder ─────────────────────────────────────────────────────────────

const Dynamicformbuilder = (props) => {
  const [formTitle, setFormTitle] = useState(props?.title || "");
  const [formDescription, setFormDescription] = useState(
    props?.description || "",
  );
  const [items, setItems] = useState(props?.items || []);
  const [activeItemId, setActiveItemId] = useState(null);
  const [permissionModal, setPermissionModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [menuTop, setMenuTop] = useState(160);

  const initRole = (code) => {
    const val = ROLE_CODE_TO_MAP[code];
    return val ? USER_ROLES.find((r) => r.value === val) : null;
  };

  const [evaluatorRole, setEvaluatorRole] = useState(
    initRole(props?.evaluatorRoleCode),
  );
  const [evaluateeRole, setEvaluateeRole] = useState(
    initRole(props?.evaluateeRoleCode),
  );
  const [allowSelfEval, setAllowSelfEval] = useState(false);
  const [contextSelections, setContextSelections] = useState({});
  const [nextTermTarget, setNextTermTarget] = useState("");
  const [isOpen, setIsOpen] = useState(props?.isOpen || false);
  const [openDate, setOpenDate] = useState(props?.openDate || "");
  const [closeDate, setCloseDate] = useState(props?.closeDate || "");
  const calendarMode = localStorage.getItem("calendar-mode") || "bs";

  const containerRef = useRef(null);

  const repositionFloatingIcon = (id) => {
    const actvId = id ?? activeItemId;
    if (!actvId) return;
    const el = document.querySelector(`[data-id="${actvId}"]`);
    const container = containerRef.current;
    if (!el || !container) return;
    const elRect = el.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    // getBoundingClientRect gives viewport-relative coords; subtract the
    // container's viewport top then add back the container's own scrollTop
    // so the value is correct even when the panel is scrolled.
    const scrollOffset = container.scrollTop || 0;
    setMenuTop(elRect.top - cRect.top + scrollOffset + elRect.height / 2 - 40);
  };

  useEffect(() => {
    if (!!activeItemId) {
      const timer = setTimeout(() => {
        repositionFloatingIcon();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [activeItemId, items, contextSelections]); // also reposition when items change, to ensure the icon stays aligned with the active item
  useEffect(() => {
    if (items.length === 0) addItem("question");
  }, [items.length === 0]);
  useEffect(() => {
    if (!activeItemId) return;
    const el = document.querySelector(`[data-id="${activeItemId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [activeItemId]);

  const addItem = (type = "question") => {
    const newId = generateId();
    const newItem =
      type === "question"
        ? {
            id: newId,
            category: "question",
            label: "",
            type: "short_answer",
            required: false,
            options: [],
            maxNumber: 5,
          }
        : { id: newId, category: "section", label: "", description: "" };
    if (activeItemId) {
      const idx = items.findIndex((it) => it.id === activeItemId);
      const updated = [...items];
      updated.splice(idx + 1, 0, newItem);
      setItems(updated);
    } else {
      setItems((prev) => [...prev, newItem]);
    }
    setActiveItemId(newId);
  };

  const updateItem = (id, updates) =>
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...updates } : it)),
    );

  const deleteItem = (id) => {
    if (items.length <= 1) return;
    const idx = items.findIndex((it) => it.id === id);
    const updated = items.filter((it) => it.id !== id);
    const next =
      activeItemId === id
        ? updated[idx] || updated[idx - 1]
        : items.find((it) => it.id === activeItemId);
    setItems(updated);
    setActiveItemId(next?.id || null);
  };

  const duplicateItem = (item) => {
    const newId = generateId();
    const newItem = {
      ...item,
      id: newId,
      options: item.options?.map((o) => ({ ...o, id: generateId() })),
    };
    const idx = items.findIndex((it) => it.id === item.id);
    const updated = [...items];
    updated.splice(idx + 1, 0, newItem);
    setItems(updated);
    setActiveItemId(newId);
  };

  const onDragEnd = ({ source, destination }) => {
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index == source.index
    ) {
      return;
    }
    const reordered = Array.from(items);
    const [removed] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, removed);
    setItems(reordered);
  };

  const handleContextChange = (field, value) =>
    setContextSelections((prev) => ({ ...prev, [field]: value }));

  const submit = () => {
    if (!formTitle.trim()) {
      swal("Oops!", "Form title is required.");
      return;
    }
    if (!evaluatorRole) {
      swal("Oops!", "Please select an evaluator role.");
      return;
    }
    if (!evaluateeRole) {
      swal("Oops!", "Please select an evaluatee role.");
      return;
    }

    const config = CONTEXT_CONFIG[evaluatorRole.value]?.[evaluateeRole.value];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.category === "question" && !it.label.trim()) {
        swal("Oops!", `Question ${i + 1} is missing a label.`);
        return;
      }
      if (
        it.category === "question" &&
        ["multiple_choice", "checkboxes", "dropdown"].includes(it.type) &&
        (!it.options || it.options.length === 0)
      ) {
        swal("Oops!", `Question ${i + 1} needs at least one option.`);
        return;
      }
    }

    const payload = {
      id: props?.id || null,
      title: formTitle,
      description: formDescription,
      evaluatorRoleCode: ROLE_MAP_TO_CODE[evaluatorRole.value] || null,
      evaluateeRoleCode: ROLE_MAP_TO_CODE[evaluateeRole.value] || null,
      isOpen,
      openDate: openDate || null,
      closeDate: closeDate || null,
      allowSelfEvaluation: allowSelfEval,
      context: {
        teacherId: contextSelections.teacher?.value || null,
        subjectId: contextSelections.subject?.value || null,
        classId: contextSelections.class?.value || null,
        studentId: contextSelections.student?.value || null,
        managementMemberId: contextSelections.managementMember?.value || null,
        nextTermTarget: config?.fields.includes("nextTermTarget")
          ? nextTermTarget || null
          : null,
      },
      formQuestions: items.map((it, idx) =>
        it.category === "question"
          ? {
              label: it.label,
              type: it.type,
              required: it.required,
              options: it.options,
              maxNumber: it.maxNumber,
              orderIndex: idx,
              category: it.category,
            }
          : {
              label: it.label,
              description: it.description,
              orderIndex: idx,
              category: it.category,
            },
      ),
    };

    axiosPost(URL.insertDynamicForm, payload, (response) => {
      const { onSuccess } = props || {};
      if (response.status === 200) {
        setItems([]);
        addItem("question");
        setFormTitle("");
        setFormDescription("");
        props?.onSuccess && onSuccess();
        if (props?.onClose) props.onClose();
        swal(
          "Success",
          `Form ${props?.id ? "updated" : "submitted"} successfully!`,
        );
      } else {
        swal(
          "Oops!",
          `Failed to ${props?.id ? "update" : "submit"} form. Please try again.`,
        );
      }
    });
  };

  return (
    <>
      <div className="arr-content">
        {props?.referer && props?.referer === "widget-dock" ? (
          <div className="arr-heading">
            <h1>Evaluation Form</h1>
            {checkWidgetAdminOrNot("Evaluation Forms") && (
              <button
                className="grayOutline-btn"
                onClick={() => setPermissionModal(true)}
              >
                Permissions
              </button>
            )}
          </div>
        ) : (
          (!props?.showEdit ?? <CustomPageHeader onClick={() => {}} icon="" />)
        )}

        <div className="df-builder" ref={containerRef}>
          {/* Floating add menu */}
          <div className="df-builder__floating-menu" style={{ top: menuTop }}>
            <Tooltip title="Add question" placement="right">
              <button onClick={() => addItem("question")}>
                <Add />
              </button>
            </Tooltip>
            <Tooltip title="Add section" placement="right">
              <button onClick={() => addItem("section")}>
                <Title />
              </button>
            </Tooltip>
          </div>

          {/* Form header card */}
          <div className="df-card df-card--header" style={{ marginBottom: 16 }}>
            <div className="df-card__body">
              <input
                type="text"
                className="df-input df-input--bare df-input--title"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="Form Title"
              />
              <input
                type="text"
                className="df-input df-input--bare df-input--description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Form description"
                style={{ marginTop: 12 }}
              />
            </div>
          </div>

          {/* Evaluation setup */}
          <EvaluationSetupCard
            evaluatorRole={evaluatorRole}
            evaluateeRole={evaluateeRole}
            onEvaluatorChange={(v) => {
              setEvaluatorRole(v);
              setContextSelections({});
              setNextTermTarget("");
            }}
            onEvaluateeChange={(v) => {
              setEvaluateeRole(v);
              setContextSelections({});
              setNextTermTarget("");
            }}
            contextData={{
              teachers: [],
              subjects: [],
              classes: [],
              students: [],
              managementMembers: [],
            }}
            contextSelections={contextSelections}
            onContextChange={handleContextChange}
            nextTermTarget={nextTermTarget}
            onNextTermTargetChange={setNextTermTarget}
            allowSelfEval={allowSelfEval}
            onAllowSelfEvalChange={setAllowSelfEval}
          />

          {/* Form settings */}
          <div className="df-card" style={{ marginBottom: 16 }}>
            <p
              className="df-label"
              style={{
                marginBottom: 0,
                padding: "14px 20px",
                borderBottom: "1px solid #e2e8f0",
                fontSize: "0.8rem",
                fontWeight: "600",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Form Settings
            </p>
            <div style={{ padding: 20 }} className="df-card__body">
              <div style={{ margin: 0 }} className="df-settings__grid">
                <div>
                  <label className="df-label">Form Status</label>
                  <div className="df-settings__toggle-row">
                    <input
                      type="checkbox"
                      checked={isOpen}
                      onChange={(e) => setIsOpen(e.target.checked)}
                    />
                    <span className="df-settings__toggle-label">
                      {isOpen ? "Open" : "Closed"}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="df-label">Open Date</label>
                  <NepaliDatePicker
                    onDateSelect={(date) => setOpenDate(date.ad.date)}
                    value={
                      moment(openDate).isValid()
                        ? moment(openDate).format("YYYY-MM-DD")
                        : ""
                    }
                    calendarMode={calendarMode}
                  />
                </div>

                <div>
                  <label className="df-label">Close Date</label>
                  <NepaliDatePicker
                    onDateSelect={(date) => setCloseDate(date.ad.date)}
                    value={
                      moment(closeDate).isValid()
                        ? moment(closeDate).format("YYYY-MM-DD")
                        : ""
                    }
                    calendarMode={calendarMode}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Drag-drop items */}
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="items-list">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="df-builder__items"
                >
                  {items.map((it, i) => (
                    <Draggable
                      key={String(it.id)}
                      draggableId={String(it.id)}
                      index={i}
                    >
                      {(provided) =>
                        it.category === "section" ? (
                          <div style={{ marginBottom: 16 }}>
                            <SectionCard
                              innerRef={provided.innerRef}
                              provided={provided}
                              section={it}
                              isActive={activeItemId === it.id}
                              onClick={() => setActiveItemId(it.id)}
                              onUpdate={updateItem}
                              onDelete={deleteItem}
                            />
                          </div>
                        ) : (
                          <div style={{ marginBottom: 16 }}>
                            <QuestionCard
                              innerRef={provided.innerRef}
                              provided={provided}
                              question={it}
                              index={i}
                              isActive={activeItemId === it.id}
                              onClick={() => setActiveItemId(it.id)}
                              onUpdate={updateItem}
                              onDelete={deleteItem}
                              onDuplicate={duplicateItem}
                            />
                          </div>
                        )
                      }
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {/* Bottom actions */}
          <div className="df-builder__actions">
            <button
              className="grayOutline-btn"
              style={{
                padding: "8px 20px",
                width: "fit-content",
              }}
              onClick={() => setShowPreview(true)}
            >
              <Visibility fontSize="small" /> Preview
            </button>
            <button
              className="primary-btn"
              style={{
                padding: "8px 20px",
                width: "fit-content",
              }}
              onClick={submit}
            >
              Submit
            </button>
          </div>
        </div>
      </div>

      {/* Preview modal */}
      <ModalWindow
        modal={showPreview}
        size="lg"
        id="tt-previewModal"
        toggleModal={() => setShowPreview(false)}
        modalHeader="Preview"
        modalBody={
          <DynamicFormDisplay
            previewOnly={true}
            onClose={() => setShowPreview(false)}
            title={formTitle}
            description={formDescription}
            items={items}
            formMeta={{
              id: props?.id,
              respondentRole: evaluatorRole?.value,
              evaluateeRole: evaluateeRole?.value,
            }}
          />
        }
      />

      {/* Permission modal */}
      <ModalWindow
        modal={permissionModal}
        size="lg"
        id="tt-permissionModal"
        toggleModal={() => setPermissionModal(false)}
        modalHeader="Assign permission to user"
        modalBody={
          <GranularPermissionModal
            widgetName="Evaluation Form"
            moduleName="Form Builder"
            header="Add Evaluation Form"
            activityName="create"
          />
        }
      />
    </>
  );
};

export default Dynamicformbuilder;
